import React, { useMemo } from 'react';

import { SectionCard, TermTooltip } from '~components';
import FigureTileGroup from '~components/visualisation/figure-tile/FigureTileGroup';
import SectionInfo from '~support-at-home/components/common/SectionInfo';

import { DashboardBudgetOverview, DashboardBudgetsSummary } from '~support-at-home/models/dashboard';

import { DASHBOARD_TEXT } from './dashboardText';
import { DashboardWidgetStatus, READY_WIDGET } from './dashboardWidgets';
import { DashboardWidgetState } from './DashboardWidgetState';
import { buildAllocationTiles, buildUtilisationTiles } from './utils';

interface ComponentProps {
  summary?: DashboardBudgetsSummary;
  overview?: DashboardBudgetOverview;
  /** Loading or failed: the state box replaces the tiles, under the title. Default ready. */
  status?: DashboardWidgetStatus;
}

const copy = DASHBOARD_TEXT.summary;

/**
 * Company Summary (TAV-3342, rebuilt TAV-3463): the Budget Tracker's two tile rows over the whole company. Allocation
 * is Available = Allocated + Residual; Utilisation is Allocated = Booked + Claimed + Remaining, then the rate.
 * Residual and Remaining are netted across budgets, and the rates come from the totals; the lists below show each budget on its own.
 */
export const DashboardSummary: React.FC<ComponentProps> = ({ summary, overview, status = READY_WIDGET }) => {
  const allocation = useMemo(() => (summary ? buildAllocationTiles(summary) : []), [summary]);
  const utilisation = useMemo(
    () => (summary && overview ? buildUtilisationTiles(overview, summary) : []),
    [overview, summary],
  );

  return (
    <SectionCard
      title={copy.title}
      description={
        <>
          {copy.description.before}
          <TermTooltip id="dashboard-active-status" label={copy.description.term} content={copy.description.termHint} />
          {copy.description.after}
        </>
      }
      titleAddon={<SectionInfo id="dashboard-summary-section" label={copy.title} content={copy.section} />}
      className="dashboard-summary"
      dataCy="dashboard-summary"
    >
      {status.phase !== 'ready' || !summary ? (
        <DashboardWidgetState status={status} title={copy.title} dataCy="dashboard-summary" />
      ) : (
        <div className="tracker-summary-groups">
          <FigureTileGroup
            title={copy.allocationTitle}
            subtitle={copy.allocationSubtitle}
            titleTag="h6"
            items={allocation}
            dataCy="dashboard-summary-allocation"
          />
          <FigureTileGroup
            title={copy.utilisationTitle}
            subtitle={copy.utilisationSubtitle}
            titleTag="h6"
            items={utilisation}
            dataCy="dashboard-summary-utilisation"
          />
        </div>
      )}
    </SectionCard>
  );
};
